import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
    BookOpen, Plus, Edit, Trash2, Loader2, Eye, Calendar,
    Save, X, Image, Tag, Search, FileText
} from 'lucide-react';
import { apiRequest, API_URL } from '../../lib/utils';
import { toast } from 'sonner';

const CATEGORIES = [
    'Traitement',
    'Entretien', 
    'Problèmes',
    'Saisonnier',
    'Équipement',
    'Conseils'
];

export default function ArticlesSection() {
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingArticle, setEditingArticle] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [uploadingImage, setUploadingImage] = useState(false);
    const fileInputRef = useRef(null);

    const [form, setForm] = useState({
        title: '',
        slug: '',
        excerpt: '',
        content: '',
        category: 'Traitement',
        tags: '',
        image_url: '',
        meta_title: '',
        meta_description: '',
        published: false
    });

    useEffect(() => {
        loadArticles();
    }, []);

    const loadArticles = async () => {
        setLoading(true);
        try {
            const data = await apiRequest('/blog/admin/articles');
            setArticles(data);
        } catch (error) {
            toast.error('Erreur lors du chargement des articles');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setForm({
            title: '',
            slug: '',
            excerpt: '',
            content: '',
            category: 'Traitement',
            tags: '',
            image_url: '',
            meta_title: '',
            meta_description: '',
            published: false
        });
        setEditingArticle(null);
    };

    const generateSlug = (title) => {
        return title
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/(^-|-$)/g, '');
    };

    const handleTitleChange = (value) => {
        setForm(prev => ({
            ...prev,
            title: value,
            slug: editingArticle ? prev.slug : generateSlug(value)
        }));
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploadingImage(true);
        try {
            const formData = new FormData();
            formData.append('file', file);

            const token = localStorage.getItem('auth_token');
            const response = await fetch(`${API_URL}/upload/image`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });

            if (!response.ok) throw new Error('Erreur upload');

            const data = await response.json();
            // Remove /api prefix since API_URL already includes it
            const imageUrl = `${API_URL}${data.url.replace('/api/', '/')}`;
            setForm(prev => ({ ...prev, image_url: imageUrl }));
            toast.success('Image uploadée !');
        } catch (error) {
            toast.error(error.message);
        } finally {
            setUploadingImage(false);
        }
    };

    const handleSave = async () => {
        if (!form.title || !form.slug || !form.content) {
            toast.error('Veuillez remplir le titre, le slug et le contenu');
            return;
        }

        setSaving(true);
        try {
            const payload = {
                ...form,
                tags: form.tags.split(',').map(t => t.trim()).filter(t => t)
            };

            if (editingArticle) {
                await apiRequest(`/blog/admin/articles/${editingArticle.id}`, {
                    method: 'PUT',
                    body: JSON.stringify(payload)
                });
                toast.success('Article mis à jour !');
            } else {
                await apiRequest('/blog/admin/articles', {
                    method: 'POST',
                    body: JSON.stringify(payload)
                });
                toast.success('Article créé !');
            }

            setDialogOpen(false);
            resetForm();
            loadArticles();
        } catch (error) {
            toast.error(error.message);
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (article) => {
        setForm({
            title: article.title,
            slug: article.slug,
            excerpt: article.excerpt || '',
            content: article.content || '',
            category: article.category || 'Traitement',
            tags: article.tags?.join(', ') || '',
            image_url: article.image_url || '',
            meta_title: article.meta_title || '',
            meta_description: article.meta_description || '',
            published: article.published || false
        });
        setEditingArticle(article);
        setDialogOpen(true);
    };

    const handleDelete = async (article) => {
        if (!confirm(`Supprimer l'article "${article.title}" ?`)) return;

        try {
            await apiRequest(`/blog/admin/articles/${article.id}`, {
                method: 'DELETE'
            });
            toast.success('Article supprimé');
            loadArticles();
        } catch (error) {
            toast.error(error.message);
        }
    };

    const handleTogglePublish = async (article) => {
        try {
            await apiRequest(`/blog/admin/articles/${article.id}`, {
                method: 'PUT',
                body: JSON.stringify({ published: !article.published })
            });
            toast.success(article.published ? 'Article dépublié' : 'Article publié');
            loadArticles();
        } catch (error) {
            toast.error(error.message);
        }
    };

    const filteredArticles = articles.filter(a =>
        a.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        a.category?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    if (loading) {
        return (
            <Card className="glass rounded-2xl">
                <CardContent className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </CardContent>
            </Card>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <h2 className="font-heading text-2xl font-bold flex items-center gap-2">
                        <BookOpen className="h-6 w-6 text-primary" />
                        Gestion des articles
                    </h2>
                    <p className="text-muted-foreground">
                        Créez et gérez vos articles de blog pour le SEO
                    </p>
                </div>

                <Dialog open={dialogOpen} onOpenChange={(open) => {
                    setDialogOpen(open);
                    if (!open) resetForm();
                }}>
                    <DialogTrigger asChild>
                        <Button className="rounded-full">
                            <Plus className="h-4 w-4 mr-2" />
                            Nouvel article
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle>
                                {editingArticle ? 'Modifier l\'article' : 'Nouvel article'}
                            </DialogTitle>
                        </DialogHeader>

                        <div className="grid gap-6 py-4">
                            {/* Title & Slug */}
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Titre *</Label>
                                    <Input
                                        value={form.title}
                                        onChange={(e) => handleTitleChange(e.target.value)}
                                        placeholder="Comment entretenir son filtre à sable"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Slug (URL) *</Label>
                                    <Input
                                        value={form.slug}
                                        onChange={(e) => setForm(prev => ({ ...prev, slug: e.target.value }))}
                                        placeholder="entretien-filtre-sable"
                                    />
                                </div>
                            </div>

                            {/* Category & Tags */}
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Catégorie</Label>
                                    <Select
                                        value={form.category}
                                        onValueChange={(value) => setForm(prev => ({ ...prev, category: value }))}
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {CATEGORIES.map(cat => (
                                                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label>Tags (séparés par des virgules)</Label>
                                    <Input
                                        value={form.tags}
                                        onChange={(e) => setForm(prev => ({ ...prev, tags: e.target.value }))}
                                        placeholder="filtre à sable, backwash, entretien"
                                    />
                                </div>
                            </div>

                            {/* Image */}
                            <div className="space-y-2">
                                <Label>Image de couverture</Label>
                                <div className="flex gap-4 items-start">
                                    {form.image_url && (
                                        <img 
                                            src={form.image_url} 
                                            alt="Preview" 
                                            className="w-32 h-20 object-cover rounded-lg"
                                        />
                                    )}
                                    <div className="flex-1">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            ref={fileInputRef}
                                            onChange={handleImageUpload}
                                            className="hidden"
                                        />
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={uploadingImage}
                                        >
                                            {uploadingImage ? (
                                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                            ) : (
                                                <Image className="h-4 w-4 mr-2" />
                                            )}
                                            {form.image_url ? 'Changer l\'image' : 'Ajouter une image'}
                                        </Button>
                                    </div>
                                </div>
                            </div>

                            {/* Excerpt */}
                            <div className="space-y-2">
                                <Label>Résumé (affiché dans la liste)</Label>
                                <Textarea
                                    value={form.excerpt}
                                    onChange={(e) => setForm(prev => ({ ...prev, excerpt: e.target.value }))}
                                    placeholder="Court résumé de l'article..."
                                    rows={2}
                                />
                            </div>

                            {/* Content */}
                            <div className="space-y-2">
                                <Label>Contenu * (Markdown supporté)</Label>
                                <Textarea
                                    value={form.content}
                                    onChange={(e) => setForm(prev => ({ ...prev, content: e.target.value }))}
                                    placeholder="# Titre&#10;&#10;Votre contenu ici...&#10;&#10;## Sous-titre&#10;&#10;- Point 1&#10;- Point 2"
                                    rows={12}
                                    className="font-mono text-sm"
                                />
                                <p className="text-xs text-muted-foreground">
                                    Utilisez Markdown : # Titre, ## Sous-titre, **gras**, *italique*, - liste
                                </p>
                            </div>

                            {/* SEO */}
                            <div className="border-t pt-4">
                                <h3 className="font-semibold mb-4 flex items-center gap-2">
                                    <Search className="h-4 w-4" />
                                    SEO (optionnel)
                                </h3>
                                <div className="grid gap-4">
                                    <div className="space-y-2">
                                        <Label>Meta Title</Label>
                                        <Input
                                            value={form.meta_title}
                                            onChange={(e) => setForm(prev => ({ ...prev, meta_title: e.target.value }))}
                                            placeholder="Titre pour Google (60 caractères max)"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Meta Description</Label>
                                        <Textarea
                                            value={form.meta_description}
                                            onChange={(e) => setForm(prev => ({ ...prev, meta_description: e.target.value }))}
                                            placeholder="Description pour Google (160 caractères max)"
                                            rows={2}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Publish */}
                            <div className="flex items-center justify-between border-t pt-4">
                                <div className="flex items-center gap-2">
                                    <Switch
                                        checked={form.published}
                                        onCheckedChange={(checked) => setForm(prev => ({ ...prev, published: checked }))}
                                    />
                                    <Label>Publier l'article</Label>
                                </div>

                                <div className="flex gap-2">
                                    <Button
                                        variant="outline"
                                        onClick={() => {
                                            setDialogOpen(false);
                                            resetForm();
                                        }}
                                    >
                                        Annuler
                                    </Button>
                                    <Button onClick={handleSave} disabled={saving}>
                                        {saving ? (
                                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        ) : (
                                            <Save className="h-4 w-4 mr-2" />
                                        )}
                                        {editingArticle ? 'Mettre à jour' : 'Créer'}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Search */}
            <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                    placeholder="Rechercher un article..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 rounded-full"
                />
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <Card className="glass rounded-xl">
                    <CardContent className="p-4 text-center">
                        <p className="text-2xl font-bold">{articles.length}</p>
                        <p className="text-sm text-muted-foreground">Total</p>
                    </CardContent>
                </Card>
                <Card className="glass rounded-xl">
                    <CardContent className="p-4 text-center">
                        <p className="text-2xl font-bold text-green-500">
                            {articles.filter(a => a.published).length}
                        </p>
                        <p className="text-sm text-muted-foreground">Publiés</p>
                    </CardContent>
                </Card>
                <Card className="glass rounded-xl">
                    <CardContent className="p-4 text-center">
                        <p className="text-2xl font-bold text-yellow-500">
                            {articles.filter(a => !a.published).length}
                        </p>
                        <p className="text-sm text-muted-foreground">Brouillons</p>
                    </CardContent>
                </Card>
                <Card className="glass rounded-xl">
                    <CardContent className="p-4 text-center">
                        <p className="text-2xl font-bold text-blue-500">
                            {articles.reduce((sum, a) => sum + (a.views || 0), 0)}
                        </p>
                        <p className="text-sm text-muted-foreground">Vues totales</p>
                    </CardContent>
                </Card>
            </div>

            {/* Articles List */}
            <Card className="glass rounded-2xl">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <FileText className="h-5 w-5" />
                        Articles ({filteredArticles.length})
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {filteredArticles.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                            <BookOpen className="h-12 w-12 mx-auto mb-4 opacity-50" />
                            <p>Aucun article trouvé</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {filteredArticles.map(article => (
                                <div 
                                    key={article.id}
                                    className="flex items-center gap-4 p-4 bg-muted/50 rounded-xl"
                                >
                                    {/* Image */}
                                    {article.image_url ? (
                                        <img 
                                            src={article.image_url}
                                            alt={article.title}
                                            className="w-20 h-14 object-cover rounded-lg flex-shrink-0"
                                        />
                                    ) : (
                                        <div className="w-20 h-14 bg-muted rounded-lg flex items-center justify-center flex-shrink-0">
                                            <FileText className="h-6 w-6 text-muted-foreground" />
                                        </div>
                                    )}

                                    {/* Info */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <h3 className="font-semibold truncate">{article.title}</h3>
                                            {article.published ? (
                                                <Badge variant="default" className="bg-green-500">Publié</Badge>
                                            ) : (
                                                <Badge variant="secondary">Brouillon</Badge>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                            <span className="flex items-center gap-1">
                                                <Tag className="h-3 w-3" />
                                                {article.category}
                                            </span>
                                            <span className="flex items-center gap-1">
                                                <Calendar className="h-3 w-3" />
                                                {formatDate(article.created_at)}
                                            </span>
                                            <span className="flex items-center gap-1">
                                                <Eye className="h-3 w-3" />
                                                {article.views || 0} vues
                                            </span>
                                        </div>
                                    </div>

                                    {/* Actions */}
                                    <div className="flex items-center gap-2">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleTogglePublish(article)}
                                            title={article.published ? 'Dépublier' : 'Publier'}
                                        >
                                            <Eye className={`h-4 w-4 ${article.published ? 'text-green-500' : ''}`} />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleEdit(article)}
                                        >
                                            <Edit className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleDelete(article)}
                                            className="text-destructive hover:text-destructive"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
